// Progress Timeline - Show player's position changes over time
import React from 'react';

const ProgressTimeline = ({ timelineData, categories }) => {
  if (!timelineData || timelineData.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Not enough games played yet to show timeline
      </div>
    );
  }

  const getPositionColor = (rank) => {
    if (rank === 1) return 'text-yellow-600';
    if (rank === 2) return 'text-gray-500';
    if (rank === 3) return 'text-orange-600';
    return 'text-gray-700';
  };

  const getPositionIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  return (
    <div>
      {/* Scroll hint for mobile */}
      {timelineData.length > 3 && (
        <div className="mb-2 text-center lg:hidden">
          <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
            <span>👈</span> Scroll to see more <span>👉</span>
          </p>
        </div>
      )}

      <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        <div className="min-w-full">
          {/* Overall Timeline */}
          <div className="mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span className="text-xl">🏆</span>
              Overall Position
            </h4>
            <div className="flex items-center gap-2 overflow-x-auto pb-2 snap-x snap-mandatory">
              {timelineData.map((point, index) => (
                <div key={index} className="flex flex-col items-center min-w-[80px] snap-center">
                  <div
                    className={`w-12 h-12 rounded-full border-4 flex items-center justify-center font-bold text-sm ${
                      index === timelineData.length - 1
                        ? 'bg-blue-100 border-blue-500 text-blue-700'
                        : 'bg-white border-gray-300'
                    }`}
                  >
                    {point.overall.rank ? getPositionIcon(point.overall.rank) : '-'}
                  </div>
                  {index < timelineData.length - 1 && (
                    <div className="w-full h-1 bg-gray-200 mt-6 absolute" style={{ width: '60px', marginLeft: '70px' }} />
                  )}
                  <div className="text-xs text-gray-600 mt-2 text-center whitespace-nowrap">
                    {point.date}
                  </div>
                  <div className="text-xs text-gray-500">{point.overall.points}pts</div>
                </div>
              ))}
            </div>
          </div>

          {/* Category Timelines */}
          {categories.map(category => {
            const hasCategoryData = timelineData.some(point => point.categories[category.id]);
            if (!hasCategoryData) return null;

            return (
              <div key={category.id} className="mb-6">
                <h4
                  className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2"
                  style={{ color: category.color }}
                >
                  <span className="text-xl">{category.icon}</span>
                  {category.name}
                </h4>
                <div className="flex items-center gap-2 overflow-x-auto pb-2 snap-x snap-mandatory">
                  {timelineData.map((point, index) => {
                    const catData = point.categories[category.id];
                    return (
                      <div key={index} className="flex flex-col items-center min-w-[80px] snap-center">
                        <div
                          className={`w-12 h-12 rounded-full border-4 flex items-center justify-center font-bold text-sm ${
                            index === timelineData.length - 1
                              ? `bg-opacity-20 border-4`
                              : 'bg-white border-gray-300'
                          }`}
                          style={{
                            backgroundColor: index === timelineData.length - 1 ? `${category.color}30` : undefined,
                            borderColor: index === timelineData.length - 1 ? category.color : undefined
                          }}
                        >
                          {catData?.rank ? getPositionIcon(catData.rank) : '-'}
                        </div>
                        <div className="text-xs text-gray-600 mt-2 text-center whitespace-nowrap">
                          {point.date}
                        </div>
                        <div className="text-xs text-gray-500">
                          {catData?.points ? `${catData.points}pts` : '-'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Scroll indicators */}
      {timelineData.length > 3 && (
        <div className="mt-3 flex justify-center gap-1 lg:hidden">
          {timelineData.map((_, index) => (
            <div
              key={index}
              className="w-2 h-2 rounded-full bg-gray-300"
              aria-hidden="true"
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProgressTimeline;
